/**
 * Data Import service client — typed API calls for the admin Data Import section.
 *
 * Handles file uploads via FormData for preview and process endpoints,
 * and standard JSON for types and history endpoints.
 *
 * Requirements: 48.1–48.13
 */

// ─── Types ─────────────────────────────────────────────────────────────

export interface ImportType {
  id: string;
  label: string;
  description: string;
  acceptedFormats: string;
  isRecurring: boolean;
  requiredRole: string;
}

export interface ImportPreview {
  totalRows: number;
  sampleRows: Record<string, unknown>[];
  headers: string[];
  importType: string;
}

export interface ImportResult {
  importType: string;
  fileName: string;
  totalProcessed: number;
  created: number;
  updated: number;
  errors: Array<{ row?: number; error: string }>;
  timestamp: string;
}

export interface ImportHistoryEntry {
  importType: string;
  fileName: string;
  importedBy: string;
  importedAt: string;
  totalProcessed: number;
  created: number;
  updated: number;
  errors: number;
}

// ─── API Client ────────────────────────────────────────────────────────

/**
 * Fetches the list of available import types for the current user.
 */
async function getImportTypes(): Promise<ImportType[]> {
  const response = await fetch('/api/mgmt/import/types', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.error?.message ?? 'Failed to fetch import types.');
  }

  return response.json();
}

/**
 * Uploads a file for preview without importing.
 * Returns total rows, sample data, and detected headers.
 */
async function previewImport(file: File, importType: string): Promise<ImportPreview> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('importType', importType);

  const response = await fetch('/api/mgmt/import/preview', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.error?.message ?? 'Failed to preview import file.');
  }

  return response.json();
}

/**
 * Starts an import and polls for completion.
 * The API returns immediately with a jobId, then we poll until done.
 */
async function processImport(file: File, importType: string): Promise<ImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('importType', importType);
  formData.append('fileName', file.name);

  // Step 1: Start the import (returns immediately with jobId)
  const startResponse = await fetch('/api/mgmt/import/process', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!startResponse.ok) {
    const data = await startResponse.json().catch(() => ({}));
    throw new Error(data?.error?.message ?? data?.message ?? 'Failed to start import.');
  }

  const startData = await startResponse.json();
  const jobId = startData.jobId;

  if (!jobId) {
    // Fallback: if the API returned the result directly (old behavior)
    if (startData.totalProcessed !== undefined) return startData as ImportResult;
    throw new Error('No job ID returned from import.');
  }

  // Step 2: Poll for status every 2 seconds
  for (let attempt = 0; attempt < 300; attempt++) { // Max 10 minutes
    await new Promise(resolve => setTimeout(resolve, 2000));

    const statusResponse = await fetch(`/api/mgmt/import/status/${jobId}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (!statusResponse.ok) {
      throw new Error('Failed to check import status.');
    }

    const statusData = await statusResponse.json();

    if (statusData.status === 'done') {
      return statusData.result as ImportResult;
    }

    if (statusData.status === 'error') {
      throw new Error(statusData.error ?? 'Import failed.');
    }

    // Still processing — continue polling
  }

  throw new Error('Import timed out after 10 minutes.');
}

/**
 * Fetches import history from AuditLog.
 */
async function getImportHistory(): Promise<ImportHistoryEntry[]> {
  const response = await fetch('/api/mgmt/import/history', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.error?.message ?? 'Failed to fetch import history.');
  }

  return response.json();
}

export const dataImportService = {
  getImportTypes,
  previewImport,
  processImport,
  getImportHistory,
};
